'use client'

import { type ComponentProps, useCallback, useMemo, useState } from 'react'

import { Dialog, Heading, Paragraph } from '@graftcode/design-system'

import { MEMBERS_INVITE_DIALOG_TEST_IDS } from '@common/common-test-ids.const'
import { DialogFooter } from '@common/components/DialogFooter/DialogFooter'

import { MemberLevel } from '@modules/projects/project-members.enum'
import type { WorkspaceMember, WorkspaceMemberRole } from '@modules/projects/project-members.interface'
import { findInviteEmailConflict } from '@modules/settings/utils/findInviteEmailConflict'
import { getInviteConflictValidationMessage } from '@modules/settings/utils/getInviteConflictValidationMessage'
import { InviteMemberFlowError } from '@modules/settings/utils/InviteMemberFlowError'

import { EMAIL_PATTERN } from './members-invite-dialog.const'
import { MembersInviteTagInput } from './MembersInviteTagInput'

import styles from './MembersInviteDialog.module.scss'

const DEFAULT_ROLE = MemberLevel.DEVELOPER

type FormSubmitHandler = NonNullable<ComponentProps<'form'>['onSubmit']>

type MembersInviteDialogProps = {
  cancelLabel: string
  descriptionBreak?: boolean
  confirmLabel: string
  conflictAlreadyInvitedMessage: string
  conflictAlreadyMemberMessage: string
  contextName: string
  descriptionMid: string
  descriptionPost: string
  descriptionPre: string
  emailInvalidMessage: string
  membersForValidation?: WorkspaceMember[]
  onInvite: (email: string, role: WorkspaceMemberRole) => Promise<void>
  onOpenChange: (open: boolean) => void
  open: boolean
  placeholder: string
  roleAdminLabel: string
  roleDeveloperLabel: string
  title: string
}

export function MembersInviteDialog({
  cancelLabel,
  descriptionBreak = false,
  confirmLabel,
  conflictAlreadyInvitedMessage,
  conflictAlreadyMemberMessage,
  contextName,
  descriptionMid,
  descriptionPost,
  descriptionPre,
  emailInvalidMessage,
  membersForValidation = [],
  onInvite,
  onOpenChange,
  open,
  placeholder,
  roleAdminLabel,
  roleDeveloperLabel,
  title,
}: MembersInviteDialogProps) {
  const [emails, setEmails] = useState<string[]>([])
  const [role, setRole] = useState<WorkspaceMemberRole>(DEFAULT_ROLE as WorkspaceMemberRole)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const email = emails[0] ?? ''
  const isEmailValid = email !== '' && EMAIL_PATTERN.test(email)
  const inviteConflict = useMemo(
    () => (isSubmitting ? null : findInviteEmailConflict(membersForValidation, email)),
    [email, isSubmitting, membersForValidation]
  )
  const conflictMessage = useMemo(
    () =>
      getInviteConflictValidationMessage(inviteConflict, {
        alreadyMember: conflictAlreadyMemberMessage,
        alreadyInvited: conflictAlreadyInvitedMessage,
      }),
    [conflictAlreadyInvitedMessage, conflictAlreadyMemberMessage, inviteConflict]
  )
  const hasInviteConflict = inviteConflict !== null

  const roleOptions = useMemo(
    () => [
      { id: DEFAULT_ROLE, label: roleDeveloperLabel, type: 'item' as const },
      { id: MemberLevel.ADMIN, label: roleAdminLabel, type: 'item' as const },
    ],
    [roleAdminLabel, roleDeveloperLabel]
  )

  const activeRoleLabel = roleOptions.find((option) => option.id === role)?.label ?? roleDeveloperLabel

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isSubmitting) return
      if (!nextOpen) {
        setEmails([])
        setRole(DEFAULT_ROLE as WorkspaceMemberRole)
      }
      onOpenChange(nextOpen)
    },
    [isSubmitting, onOpenChange]
  )

  const handleSubmit: FormSubmitHandler = async (event) => {
    event.preventDefault()

    if (emails.length === 0 || !isEmailValid || hasInviteConflict) {
      return
    }

    setIsSubmitting(true)
    try {
      for (const email of emails) {
        await onInvite(email.trim(), role)
      }
      handleOpenChange(false)
    } catch (error) {
      if (error instanceof InviteMemberFlowError && error.closeDialog) {
        handleOpenChange(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      noPadding
      showDismiss
      open={open}
      onOpenChange={handleOpenChange}
      ariaLabelledBy="members-invite-title"
      ariaDescribedBy="members-invite-description"
      className={styles.dialog}
    >
      <form
        className={styles.dialog}
        onSubmit={handleSubmit}
        data-testid={open ? MEMBERS_INVITE_DIALOG_TEST_IDS.root : undefined}
      >
        <div className={styles.header}>
          <Heading type="h4" id="members-invite-title">
            {title}
          </Heading>
        </div>

        <div className={styles.content} id="members-invite-description">
          <Paragraph size="sm" className={styles.description}>
            {descriptionPre}
            <span className={styles.boldText}>{contextName}</span>
            {descriptionMid}
            {descriptionBreak ? <br /> : null}
            {descriptionPost}
          </Paragraph>

          <MembersInviteTagInput
            emails={emails}
            conflictMessage={conflictMessage}
            emailInvalidMessage={emailInvalidMessage}
            onEmailsChange={setEmails}
            roleLabel={activeRoleLabel}
            roleOptions={roleOptions}
            onRoleSelect={(id) => setRole(id as WorkspaceMemberRole)}
            placeholder={placeholder}
            disabled={isSubmitting}
          />
        </div>

        <DialogFooter
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          confirmTestId={MEMBERS_INVITE_DIALOG_TEST_IDS.submit}
          disabled={!isEmailValid || hasInviteConflict}
          loading={isSubmitting}
          onCancel={() => handleOpenChange(false)}
        />
      </form>
    </Dialog>
  )
}
